import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom"; // Combined imports

import BASE_URL from "../../configs/api";
import { getProxyUrl } from "../../utils/proxy";

import { FiChevronLeft, FiChevronRight, FiFilm } from "react-icons/fi"; // Added FiFilm, FiAlertTriangle

const useRecommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { slug } = useParams(); // slug of the current movie

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!slug) {
        setLoading(false);
        setRecommendations([]);
        return;
      }
      try {
        setLoading(true);
        // Fetch current movie to get its genre
        const resMovie = await axios.get(`${BASE_URL}/api/phim/${slug}/`);
        const movieData = resMovie.data;

        if (movieData?.the_loai?.length > 0) {
          const firstGenreSlug = movieData.the_loai[0].slug;
          // Fetch movies from the same genre (e.g., page 2 for variety)
          const res = await axios.get(
            `${BASE_URL}/api/phim_category/?theloai=${firstGenreSlug}&page=1&limit=24` // Fetch more items for pagination, page 1
          );
          const data = res.data;
          if (Array.isArray(data.results)) {
            // Filter out the current movie from recommendations if it appears
            setRecommendations(data.results.filter((rec) => rec.slug !== slug));
          } else {
            console.warn(
              "Recommendations data.results is not an array:",
              data.results
            );
            setRecommendations([]);
          }
        } else {
          setRecommendations([]); // No genre to base recommendations on
        }
      } catch (error) {
        console.error("Error fetching recommendations:", error);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecommendations();
  }, [slug]);

  return { recommendations, loading };
};

// --- RecommendationsList (Compact Sidebar/Aside List) ---
const RecommendationsList = ({ recommendations, loading }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-6 space-y-2">
        {[...Array(3)].map(
          (
            _,
            i // Skeleton loaders
          ) => (
            <div
              key={i}
              className="flex w-full space-x-3 p-2 bg-neutral-700/30 rounded-lg animate-pulse"
            >
              <div className="w-[4.5rem] h-[6.75rem] bg-neutral-600/50 rounded"></div>
              <div className="flex-1 py-1 space-y-2.5">
                <div className="h-4 bg-neutral-600/50 rounded w-3/4"></div>
                <div className="h-3 bg-neutral-600/50 rounded w-1/2"></div>
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  const validRecommendations = Array.isArray(recommendations)
    ? recommendations
    : [];

  if (validRecommendations.length === 0) {
    return (
      <div className="text-neutral-500 text-sm text-center py-6 px-2 flex flex-col items-center">
        <FiFilm className="w-8 h-8 mb-2 text-neutral-600" />
        Không có đề xuất nào phù hợp.
      </div>
    );
  }

  return (
    <div className="space-y-3 p-1">
      {validRecommendations.slice(0, 5).map((movie) => (
        <Link
          to={`/phim/${movie.slug}`}
          key={movie.slug || movie.id}
          className="flex space-x-3 group rounded-lg bg-black/20"
        >
          <img
            src={movie.poster_url || getProxyUrl(movie.poster || "")}
            alt={movie.ten_phim || "Poster phim"}
            className="w-20 h-32 object-cover flex-shrink-0 rounded-lg shadow-md"
            loading="lazy"
          />

          <div className="overflow-hidden flex-1 flex flex-col justify-center space-y-2 py-1">
            <h4
              className="text-sm font-semibold text-white group-hover:text-orange-400 transition-colors duration-200 line-clamp-2 leading-tight"
              title={movie.ten_phim}
            >
              {movie.ten_phim || "Tên phim không có"}
            </h4>

            {(movie.ten_khac || movie.nam_phat_hanh) && (
              <p
                className="text-xs text-gray-400 line-clamp-1"
                title={movie.ten_khac}
              >
                {movie.ten_khac || movie.nam_phat_hanh || ""}
              </p>
            )}

            <div className="flex flex-wrap gap-2 text-xs text-gray-300">
              {movie.tinh_trang && <span>{movie.tinh_trang}</span>}
              {movie.ngon_ngu && movie.tinh_trang && <span>•</span>}
              {movie.ngon_ngu && <span>{movie.ngon_ngu}</span>}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

// --- DetailRecommendationsList (Paginated Grid for main content area) ---
const DetailRecommendationsList = ({ recommendations, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Keep or adjust as needed

  const validRecommendations = Array.isArray(recommendations)
    ? recommendations
    : [];
  const totalPages = Math.ceil(validRecommendations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = validRecommendations.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const listRef = useRef(null); // For scrolling to top of list on page change

  const goToPrevPage = () => {
    setCurrentPage((p) => Math.max(p - 1, 1));
    listRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const goToNextPage = () => {
    setCurrentPage((p) => Math.min(p + 1, totalPages));
    listRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      // Enhanced Skeleton Loader for Grid
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-6 sm:gap-x-5 sm:gap-y-8">
        {[...Array(itemsPerPage)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-[2/3] bg-neutral-700/50 rounded-lg"></div>
            <div className="mt-2 h-4 bg-neutral-700/50 rounded w-3/4 mx-auto"></div>
            <div className="mt-1 h-3 bg-neutral-700/50 rounded w-1/2 mx-auto"></div>
          </div>
        ))}
      </div>
    );
  }

  if (validRecommendations.length === 0) {
    return (
      <div className="text-neutral-500 text-center py-10 flex flex-col items-center">
        <FiFilm className="w-12 h-12 mb-3 text-neutral-600" />
        <p className="text-lg">Không tìm thấy đề xuất nào.</p>
        <p className="text-sm">
          Vui lòng thử lại sau hoặc khám phá các thể loại khác.
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        ref={listRef}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-6 sm:gap-x-5 sm:gap-y-8"
      >
        {currentItems.map((movie) => (
          <Link
            to={`/phim/${movie.slug}`} // Ensure slug is used for routing
            key={movie.slug || movie.id}
            className="group block text-neutral-100 hover:text-white transition-all duration-300"
          >
            <div className="aspect-[2/3] bg-neutral-800 rounded-lg overflow-hidden shadow-lg relative border-2 border-transparent group-hover:border-yellow-500/50 transition-all duration-300">
              <img
                src={movie.poster_url || getProxyUrl(movie.poster || "")}
                alt={movie.ten_phim || "Poster phim"}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://placehold.co/200x300/374151/777?text=No+Image";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-orange-500 rounded-full p-3 shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.737 4 21.277 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648z"
                    />
                  </svg>
                </div>
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex flex-row justify-center items-start z-20">
                <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white text-[10px] leading-tight md:text-xs font-semibold px-2 py-1 rounded-tl-lg shadow-md whitespace-nowrap">
                  {movie.ngon_ngu
                    ? movie.ngon_ngu
                        .replace("Vietsub", "P.Đ")
                        .replace("Lồng Tiếng", "T.M")
                        .replace("Thuyết Minh", "T.M")
                    : "Đang cập nhật"}
                </div>
                <div className="bg-gradient-to-r from-[#C02425] to-[#fd9a05] text-white text-[10px] leading-tight md:text-xs font-semibold px-2 py-1 rounded-tr-lg shadow-md whitespace-nowrap">
                  {(movie.tinh_trang || "Đang cập nhật")
                    .replace(/Hoàn Tất/gi, "HT")
                    .trim() || "Đang cập nhật"}
                </div>
              </div>
            </div>

            <div className="mt-2.5 px-1 text-center">
              <h3
                className="text-sm sm:text-base font-semibold text-neutral-100 group-hover:text-yellow-400 transition duration-200 truncate"
                title={movie.ten_phim}
              >
                {movie.ten_phim || "Không có tiêu đề"}
              </h3>
              <p
                className="text-xs sm:text-sm text-neutral-400 group-hover:text-neutral-300 transition duration-200 truncate"
                title={movie.ten_khac}
              >
                {movie.ten_khac || movie.nam_phat_hanh || ""}
              </p>
            </div>
          </Link>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-row justify-center items-center mt-10 md:mt-12 space-x-2 sm:space-x-3">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className="inline-flex items-center justify-center p-2 sm:px-3 sm:py-2 bg-neutral-700/80 text-neutral-200 rounded-md shadow-sm border border-neutral-600/70 transition-all duration-200 ease-in-out text-xs sm:text-sm hover:bg-neutral-600/90 hover:text-white hover:border-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Trang trước"
          >
            <FiChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="hidden sm:inline ml-1">Trước</span>
          </button>

          <span className="text-neutral-300 font-medium text-xs sm:text-sm px-2.5 py-1.5 sm:px-3 sm:py-2 bg-neutral-700/60 rounded-md border border-neutral-600/50">
            {`${currentPage} / ${totalPages}`}
          </span>

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="inline-flex items-center justify-center p-2 sm:px-3 sm:py-2 bg-neutral-700/80 text-neutral-200 rounded-md shadow-sm border border-neutral-600/70 transition-all duration-200 ease-in-out text-xs sm:text-sm hover:bg-neutral-600/90 hover:text-white hover:border-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Trang sau"
          >
            <span className="hidden sm:inline mr-1">Sau</span>
            <FiChevronRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          </button>
        </div>
      )}
    </>
  );
};

// --- Wrapper Sections ---
const RecommendationsSection = () => {
  // Removed phimId prop, relies on useParams in hook
  const { recommendations, loading } = useRecommendations();
  return (
    <div className="">
      <h3 className="text-lg sm:text-xl font-semibold text-neutral-100 px-1 pb-3 mb-2 flex items-center border-b-2 border-neutral-700/60">
        <FiFilm className="w-5 h-5 mr-2.5 text-orange-400 flex-shrink-0" />
        <span>Đề xuất cho bạn</span>
      </h3>
      <RecommendationsList
        recommendations={recommendations}
        loading={loading}
      />
    </div>
  );
};

const DetailRecommendationsSection = () => {
  // Removed phimId prop
  const { recommendations, loading } = useRecommendations();
  return (
    <div className="mt-8 sm:mt-12">
      <h3 className="text-xl sm:text-2xl font-bold text-neutral-100 mb-5 sm:mb-6 flex items-center pb-2 border-b-2 border-neutral-700">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 mr-3 text-orange-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
        <span>Có thể bạn sẽ thích</span>
      </h3>
      <DetailRecommendationsList
        recommendations={recommendations}
        loading={loading}
      />
    </div>
  );
};

// Make sure to export the components you intend to use elsewhere
export default RecommendationsSection; // If this is the primary export
export { DetailRecommendationsSection, RecommendationsSection }; // If exporting multiple named components
